/**
 * Carbon Emissions Panel Component
 * 
 * Displays carbon emissions data for selected vehicles in FleetIntelligence
 */

import { useState, useEffect } from 'react';
import { Leaf, AlertCircle, Info, Award } from 'lucide-react';
import { Button } from '../ui/button';
import {
  calculateVehicleCarbon,
  getFleetCarbonSummary,
  getTenantCarbonStatus,
  type VehicleCarbonEmissions,
  type FleetCarbonSummary,
  type TenantCarbonStatus
} from '../../services/carbonApi';
import type { TimeRange } from '../../tabs/FleetIntelligence';

interface CarbonPanelProps {
  selectedVehicles: string[];
  timeRange: TimeRange;
}

export default function CarbonPanel({ selectedVehicles, timeRange }: CarbonPanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicleData, setVehicleData] = useState<VehicleCarbonEmissions[]>([]);
  const [fleetSummary, setFleetSummary] = useState<FleetCarbonSummary | null>(null);
  const [tenantStatus, setTenantStatus] = useState<TenantCarbonStatus | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Load tenant status on mount
  useEffect(() => {
    loadTenantStatus();
  }, []);

  // Load carbon data when vehicles or time range changes
  useEffect(() => {
    if (selectedVehicles.length > 0) {
      loadCarbonData();
    } else {
      setVehicleData([]);
      setFleetSummary(null);
    }
  }, [selectedVehicles, timeRange]);

  const loadTenantStatus = async () => {
    try {
      const status = await getTenantCarbonStatus('default');
      setTenantStatus(status);
    } catch (err) {
      console.error('Failed to load tenant carbon status:', err);
    }
  };

  const loadCarbonData = async () => {
    setLoading(true);
    setError(null);

    try {
      const startDate = new Date(timeRange.from).toISOString().split('T')[0];
      const endDate = new Date(timeRange.to).toISOString().split('T')[0];

      // Get data for each vehicle for the selected date (using start date as reference)
      const promises = selectedVehicles.map(vehicle =>
        calculateVehicleCarbon(vehicle, startDate, 'default')
          .then(result => ({
            ...result,
            // If backend doesn't return vehicle_registration, use the selectedVehicle (which is the registration)
            vehicle_registration: result.vehicle_registration || vehicle
          }))
          .catch(err => {
            console.warn(`Failed to fetch carbon data for ${vehicle}:`, err);
            return null;
          })
      );

      const results = await Promise.all(promises);
      // The spread below narrows vehicle_registration to a definite string
      // (via the `|| vehicle` fallback), so the resolved element type is
      // stricter than VehicleCarbonEmissions's optional field — narrow
      // against the actual inferred type instead of asserting the named one.
      const validResults = results.filter((r): r is NonNullable<typeof r> => r !== null);
      setVehicleData(validResults);

      // Get fleet summary if multiple vehicles
      if (selectedVehicles.length > 1) {
        try {
          const summary = await getFleetCarbonSummary('default', {
            from: startDate,
            to: endDate
          });
          setFleetSummary(summary);
        } catch (err) {
          console.warn('Failed to fetch fleet carbon summary:', err);
        }
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load carbon data');
    } finally {
      setLoading(false);
    }
  };

  const formatEmissions = (kg: number | undefined) => {
    if (kg === undefined || kg === null || isNaN(kg)) {
      return '0 kg CO₂e';
    }
    if (kg >= 1000) {
      return `${(kg / 1000).toFixed(2)} tCO₂e`;
    }
    return `${kg.toFixed(1)} kg CO₂e`;
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A': return 'text-success bg-success/30 border-success/30';
      case 'B': return 'text-ring bg-ring/30 border-ring/30';
      case 'C': return 'text-warning bg-warning/30 border-warning/30';
      case 'D': return 'text-warning bg-warning/30 border-warning/30';
      case 'F': return 'text-destructive bg-destructive/30 border-destructive/30';
      default: return 'text-muted-foreground bg-background/30 border-border/30';
    }
  };

  const getTierLabel = (tier: number | undefined) => {
    switch (tier) {
      case 1: return 'Fuel-Based (Most Accurate)';
      case 2: return 'Distance-Based';
      case 3: return 'Estimation-Based';
      default: return 'Unknown';
    }
  };

  if (selectedVehicles.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Leaf className="w-5 h-5 text-success" />
          <h3 className="text-lg font-semibold text-white">Carbon Emissions</h3>
        </div>
        <div className="text-center text-muted-foreground py-8">
          <Leaf className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>Select vehicles to view carbon emissions data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-success" />
            <h3 className="text-lg font-semibold text-white">Carbon Emissions</h3>
            {tenantStatus && (
              <span className="text-xs px-2 py-1 rounded bg-ring/30 text-ring border border-ring/30">
                Tier {tenantStatus.expected_tier}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            onClick={() => setShowDetails(!showDetails)}
            className="h-auto px-0 gap-1 text-xs hover:text-foreground"
          >
            <Info className="w-4 h-4" />
            {showDetails ? 'Hide' : 'Show'} Details
          </Button>
        </div>
      </div>

      {/* Tenant Status Info */}
      {showDetails && tenantStatus && (
        <div className="p-4 border-b border-border bg-card/50">
          <div className="space-y-2">
            <p className="text-xs font-medium text-foreground mb-2">Configured Adapters:</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Fuel:</span>
                <span className="text-white">{tenantStatus.configured_adapters.fuel || 'Not configured'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Distance:</span>
                <span className="text-white">{tenantStatus.configured_adapters.distance || 'Not configured'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Vehicle:</span>
                <span className="text-white">{tenantStatus.configured_adapters.vehicle || 'Not configured'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Load:</span>
                <span className="text-white">{tenantStatus.configured_adapters.load || 'Not configured'}</span>
              </div>
            </div>
            {tenantStatus.missing_for_improvement.length > 0 && (
              <div className="mt-3 text-xs">
                <p className="text-warning mb-1">To improve accuracy:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                  {tenantStatus.missing_for_improvement.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="text-center text-muted-foreground py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-success mx-auto mb-2"></div>
            <p className="text-sm">Loading carbon data...</p>
          </div>
        ) : error ? (
          <div className="text-center text-destructive py-8">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">{error}</p>
          </div>
        ) : vehicleData.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No carbon data available for selected vehicles</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Fleet Summary */}
            {fleetSummary && selectedVehicles.length > 1 && (
              <div className="bg-secondary/30 rounded-lg p-4 border border-border">
                <p className="text-sm font-medium text-foreground mb-3">Fleet Carbon Summary</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Total Emissions</p>
                    <p className="text-lg font-bold text-white">
                      {formatEmissions(fleetSummary.fleet_totals?.total_co2e_kg)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Scope 1</p>
                    <p className="text-lg font-bold text-warning">
                      {formatEmissions(fleetSummary.fleet_totals?.total_scope_1_kg)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Scope 3</p>
                    <p className="text-lg font-bold text-ring">
                      {formatEmissions(fleetSummary.fleet_totals?.total_scope_3_kg)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Data Quality</p>
                    <p className={`text-lg font-bold ${getGradeColor(fleetSummary.calculation?.data_quality_grade || 'N/A')}`}>
                      {fleetSummary.calculation?.data_quality_grade || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Individual Vehicle Data */}
            <div className="space-y-3">
              {vehicleData.map((data) => (
                <div
                  key={`${data.vehicle_entity_id}-${data.date}`}
                  className="bg-secondary/20 rounded-lg p-4 border border-border hover:border-success transition-colors cursor-pointer"
                  onClick={() => setSelectedVehicle(selectedVehicle === data.vehicle_entity_id ? null : data.vehicle_entity_id)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-white">{data.vehicle_registration || data.vehicle_entity_id}</p>
                      <p className="text-xs text-muted-foreground">{new Date(data.date).toLocaleDateString('en-GB')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-white">
                        {formatEmissions(data.emissions?.co2e_kg)}
                      </p>
                      <div className="flex items-center gap-2 justify-end">
                        <span className={`text-xs px-2 py-0.5 rounded border ${getGradeColor(data.confidence?.grade || 'N/A')}`}>
                          Grade {data.confidence?.grade || 'N/A'}
                        </span>
                        {(data.confidence?.score || 0) >= 80 && (
                          <Award className="w-4 h-4 text-success" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expandable Details */}
                  {selectedVehicle === data.vehicle_entity_id && (
                    <div className="mt-3 pt-3 border-t border-border space-y-2">
                      {/* Emissions Breakdown */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Scope 1 (Tank-to-Wheel)</p>
                          <p className="text-warning font-medium">
                            {formatEmissions(data.emissions?.scope_1_kg)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Scope 3 (Well-to-Tank)</p>
                          <p className="text-ring font-medium">
                            {formatEmissions(data.emissions?.scope_3_kg)}
                          </p>
                        </div>
                      </div>

                      {/* Calculation Info */}
                      <div className="text-xs bg-card/50 p-2 rounded space-y-1">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Calculation Tier:</span>
                          <span className="text-white">{getTierLabel(data.calculation?.tier)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Method:</span>
                          <span className="text-white">{data.calculation?.method || 'Unknown'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Emission Factor:</span>
                          <span className="text-white">{data.calculation?.emission_factors?.source || 'Unknown'}</span>
                        </div>
                        {data.calculation?.emission_factors?.fuel_type && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Fuel Type:</span>
                            <span className="text-white">{data.calculation.emission_factors.fuel_type}</span>
                          </div>
                        )}
                      </div>

                      {/* Data Quality */}
                      <div className="text-xs bg-ring/20 p-2 rounded border border-ring/30">
                        <p className="text-ring font-medium mb-1">Data Quality:</p>
                        <div className="grid grid-cols-2 gap-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Fuel:</span>
                            <span className={`font-medium ${
                              data.confidence?.data_quality?.fuel_data === 'actual' ? 'text-success' :
                              data.confidence?.data_quality?.fuel_data === 'estimated' ? 'text-warning' : 'text-destructive'
                            }`}>
                              {data.confidence?.data_quality?.fuel_data || 'missing'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Distance:</span>
                            <span className={`font-medium ${
                              data.confidence?.data_quality?.distance_data === 'gps' ? 'text-success' :
                              data.confidence?.data_quality?.distance_data === 'declared' ? 'text-warning' : 'text-destructive'
                            }`}>
                              {data.confidence?.data_quality?.distance_data || 'missing'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Vehicle Spec:</span>
                            <span className={`font-medium ${
                              data.confidence?.data_quality?.vehicle_spec === 'complete' ? 'text-success' :
                              data.confidence?.data_quality?.vehicle_spec === 'partial' ? 'text-warning' : 'text-destructive'
                            }`}>
                              {data.confidence?.data_quality?.vehicle_spec || 'missing'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Load:</span>
                            <span className={`font-medium ${
                              data.confidence?.data_quality?.load_data === 'actual' ? 'text-success' :
                              data.confidence?.data_quality?.load_data === 'estimated' ? 'text-warning' : 'text-destructive'
                            }`}>
                              {data.confidence?.data_quality?.load_data || 'missing'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Data Sources */}
                      {data.data_sources && data.data_sources.length > 0 && (
                        <div className="text-xs pt-1">
                          <p className="text-muted-foreground mb-1">Data Sources:</p>
                          <div className="flex flex-wrap gap-1">
                            {data.data_sources.map((source, i) => (
                              <span
                                key={i}
                                className="px-2 py-0.5 bg-secondary text-foreground rounded text-xs"
                              >
                                {source.source} ({source.confidence}%)
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Confidence Info */}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                        <Info className="w-3 h-3" />
                        <span>
                          Confidence Score: {data.confidence?.score || 0}% (Grade {data.confidence?.grade || 'N/A'})
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
